import { ActionTree } from "vuex";
import { RootState } from "@/store/types";
import {
  AuthState,
  LoginPayload,
  SignUpPayload,
  ConfirmationPayload,
} from "../types";
import API from "@/axios";
import localforage from "localforage";
import { setAuthTokenHeader } from "../utils";

const actions: ActionTree<AuthState, RootState> = {
  login({ dispatch }, payload: LoginPayload) {
    const { email, password } = payload;
    return API.post("/auth/login", {
      email,
      password,
    }).then(async (response) => {
      const {
        data: {
          data: { token, user, refreshToken, expires },
        },
      } = response;
      if (token && user) {
        await dispatch("setToken", token);
        await dispatch("setExpires", expires);
        await dispatch("setUser", user);
        await dispatch("setRefreshToken", refreshToken);
      }
    });
  },
  signup({ dispatch }, payload: SignUpPayload) {
    const {
      firstName,
      lastName,
      email,
      password,
      vendorName,
      reCaptchaToken,
    } = payload;
    return API.post("/registration", {
      firstName,
      lastName,
      email,
      password,
      vendorName,
      reCaptchaToken,
    }).then(async () => {
      await dispatch("setVerifyEmail", email);
    });
  },
  setVerifyEmail({ commit }, email) {
    commit("setVerifyEmail", email);
  },
  setUser({ commit }, user) {
    if (user) {
      commit("setUser", user);
    }
  },
  confirmEmail(context, payload: ConfirmationPayload) {
    return API.post(`/registration/confirm_email`, {
      ...payload,
    });
  },
  resendEmail(context, email: string) {
    return API.post("/registration/resend_confirmation", { email });
  },
  async setToken({ commit }, token) {
    await localforage.setItem("token", token);
    setAuthTokenHeader(token);
    commit("setToken", token);
    commit("setAuthenticated", true);
  },
  async setRefreshToken({ commit }, refreshToken) {
    await localforage.setItem("refreshToken", refreshToken);
    commit("setRefreshToken", refreshToken);
  },
  async setExpires({ commit }, expires) {
    await localforage.setItem("expires", expires);
    commit("setExpires", expires);
  },
  checkIfUserIsAdmin({ state }, allowDevelopers: boolean) {
    const currentUser = state.user;
    if (allowDevelopers) {
      return (
        currentUser.role === 0 ||
        currentUser.role === 1 ||
        currentUser.role === 3
      );
    }
    return currentUser.role === 0 || currentUser.role === 3;
  },
  checkIfTokenExists({ commit }) {
    return localforage.getItem("token").then((token: string) => {
      if (token) {
        commit("setToken", token);
        setAuthTokenHeader(token);
        commit("setAuthenticated", true);
      } else {
        commit("setAuthenticated", false);
        throw "Auth Token not found";
      }
    });
  },
  resetPassword(context, email) {
    return API.post("/auth/password/reset", { emailAddress: email });
  },
  changePassword(
    context,
    payload: {
      userId: string;
      token: string;
      newPassword: string;
      confirmPassword: string;
    }
  ) {
    return API.post("/auth/password/confirm_reset", {
      userId: payload.userId,
      code: payload.token,
      newPassword: payload.newPassword,
      confirmPassword: payload.confirmPassword,
    });
  },
  async logout({ commit }) {
    try {
      await API.post("/auth/logout", {
        refreshToken: await localforage.getItem("refreshToken"),
      });
    } catch (ex) {
      console.log(ex);
    }

    commit("setAuthenticated", false);
    await localforage.removeItem("token");
    await localforage.removeItem("user");
    await localforage.removeItem("refreshToken");
    await localforage.clear();
    commit("reset", null, { root: true });
  },
};

export default actions;
